import { useCtxStore } from '@/stores/ctx.store'
import { PlanningGridDataView } from '@/stores/grid-data.types'
import consola from 'consola'
import { defineStore } from 'pinia'
import { type Ref, ref } from 'vue'
import { createAnnualGrowthRemainingPositions, createInitialSeasonalityEntryWhileProcessingResponse, handleAnnualGrowthPositions } from '~/composables/annual-growth'
import { getActualsUrlParamStr, getDateStr, getRangeUrlParamStr, parseYYYYMM, verifyAndGetActualsUrlParam, verifyAndGetRangeUrlParam } from '~/composables/commons'
import { type DateRangeStrategy, YearToDateStrategy } from '~/composables/date-range-strategies'
import { handleFormulaBasedPositions } from '~/composables/formula-based'
import { handleManualChangePositions } from '~/composables/manual-change.ts'
import { handleMonthlyGrowthPositions } from '~/composables/monthly-growth'
import { getLabels, getLevelIdForMethod, recalcAllCtxRows, updateCompanyPlanResValues } from '~/composables/planning-helpers'
import { type PlanningRowData, callEditAPI } from '~/composables/unified-edit-api'
import { getDisplayDateFromPlanningBegin } from '~/composables/usePlanningDateUtils'
import type { MultiGenericPayloadResponse } from '~/protos/gen/stcs_commons.entity'
import type { StcsCompActualsResValuesItem } from '~/protos/gen/stcs_comp_actuals_res_values.entity'
import { MethodologyEnum, type PlanPosItem, type StcsCompPlanCfg, type StcsCompPlanCfgItem } from '~/protos/gen/stcs_comp_plan_cfg.entity'
import type { StcsCompPlanResValues, StcsCompPlanResValuesItem } from '~/protos/gen/stcs_comp_plan_res_values.entity'
import { StcsFpnaTypeEnum, StcsFstmtEnum } from '~/protos/gen/stcs_enums.entity'
import type { StcsOrgFsdFlat } from '~/protos/gen/stcs_org_fsd_flat.entity'
import type { CompPlanCfgGetRequest } from '~/protos/gen/stcs_wapp_svc_comp_plan.ts'
import { cmwCompPlanCfgGetRequest } from '~/services/cmw.service'
import { updateI18nLabelMap } from '~/services/commons.service'
import { useFsdStore } from '~/stores/fsd.store.ts'
import type { FinancialsGridDataAvailablePlanList } from '~/types/stcs_fpna_res_values.entity'
import type { DisplayMode, FstmtView, WappCompData } from '~/types/stcs_wapp_ctx_data.entity'

type PlanningHistoryItem = {
  activeIndex: number
  lastAction: [PlanningRowData[], Record<string, StcsCompActualsResValuesItem>][]
}

type LastActionPlanList = {
  [key in StcsFstmtEnum]?: {
    [parentRowId: string]: PlanningHistoryItem // Map parent row IDs to their history
  }
}

// ─────────────────────────────────────────────────────────────
// Helper type to bundle a reactive list + its index map + id set
// ─────────────────────────────────────────────────────────────
interface ReactiveRowList {
  list: PlanningRowData[] // the reactive array (Pinia / ref)
  idxMap: Map<string, number> // row_id -> index
  idSet: Set<string> // row_id presence check
}

export interface PlanConfigCacheItem {
  stcs_comp_plan_cfg: StcsCompPlanCfg | undefined
  stcs_comp_plan_res_values: StcsCompPlanResValues | undefined
}

/** Maximum number of snapshots we keep per parent row (initial + 9 edits). */
const MAX_HISTORY_LENGTH = 12

/** Maximum number of plans to keep in memory (LRU cache) */
const MAX_CACHED_PLANS = 1

/** Maximum number of plan configs to keep in cache */
const MAX_CACHED_PLAN_CONFIGS = 1

// biome-ignore lint/complexity/noStaticOnlyClass: <explanation>
export class PlanningGridDataViewMapper {
  private static gridDataViewMap: Record<string, PlanningGridDataView> = {
    monthly: PlanningGridDataView.MonthlyView,
    yearly: PlanningGridDataView.YearlyView,
  }

  private static reverseGridDataViewMap: Record<PlanningGridDataView, DisplayMode> = {
    [PlanningGridDataView.MonthlyView]: 'monthly',
    [PlanningGridDataView.YearlyView]: 'yearly',
  }

  static stringToGridDataView(str: string): PlanningGridDataView {
    return PlanningGridDataViewMapper.gridDataViewMap[str] ?? PlanningGridDataView.MonthlyView // Default to MonthlyView if no match found
  }

  static gridDataViewToString(view: PlanningGridDataView): DisplayMode {
    return PlanningGridDataViewMapper.reverseGridDataViewMap[view] ?? 'monthly' // Default to 'monthly' if no match found
  }
}

// biome-ignore lint/complexity/noStaticOnlyClass: <explanation>
export class PlanningStatementMapper {
  private static planningStatementMap: Record<string, StcsFstmtEnum> = {
    pnl: StcsFstmtEnum.SFSE_PNL,
    bs: StcsFstmtEnum.SFSE_BS,
    cf: StcsFstmtEnum.SFSE_CF,
  }

  private static reversePlanningStatementMap: { [p: number]: string; [StcsFstmtEnum.SFSE_PNL]: string; [StcsFstmtEnum.SFSE_BS]: string; [StcsFstmtEnum.SFSE_CF]: string } = {
    [StcsFstmtEnum.SFSE_PNL]: 'pnl',
    [StcsFstmtEnum.SFSE_BS]: 'bs',
    [StcsFstmtEnum.SFSE_CF]: 'cf',
  }

  // Converts string to StcsFstmtEnum
  static stringToFinancialStatement(str: string): StcsFstmtEnum {
    return PlanningStatementMapper.planningStatementMap[str] ?? StcsFstmtEnum.SFSE_PNL // Default to ProfitLoss if no match found
  }

  // Converts StcsFstmtEnum to string
  static planningStatementToString(statement: StcsFstmtEnum): string {
    return PlanningStatementMapper.reversePlanningStatementMap[statement] ?? 'pnl' // Default to 'pnl' if no match found
  }
}

// --- Seasonality Logic Types and Store ---
export type SeasonalityLogic = {
  id: string
  name: string
  type: 'default' | 'custom'
}

export interface PlanData extends Partial<StcsCompPlanCfg> {
  id: string
  bsList: PlanningRowData[]
  pnlList: PlanningRowData[]

  // // Values maps for different statements (per-plan isolation)
  valuesMapPnl?: Record<string, StcsCompActualsResValuesItem>
  valuesMapBs?: Record<string, StcsCompActualsResValuesItem>
  valuesMapCf?: Record<string, StcsCompActualsResValuesItem>

  // // View state (per-plan)
  // activePlanningStatement?: StcsFstmtEnum
  lastActionPlanningList?: LastActionPlanList
  // selectedView: PlanningGridDataView

  // // Date ranges (per-plan)
  // planDateRange: { period_begin?: Date; period_end?: Date }
  // minDate: Date
  // maxDate: Date

  // API metadata
  display_name?: string
  fpna_begin_date_string?: string
  fpna_end_date_string?: string
  ref_id_comp_plan_cfg_template?: string
  obj_created_iso_utc?: string
  obj_updated_iso_utc?: string
  show_all_actuals?: boolean
  is_draft?: boolean
  has_plan_data?: boolean

  // Some helpers booleans
  ui_refresh_trigger?: boolean
}

export interface seas_entry_helper {
  logic_key: 'evenly' | 'l12m' | 'custom'
  seas_key: string
}

/**
 * Stores unsaved custom seasonality values (keyed by seas_key like 'custom_xxxxx')
 * Only used for custom values that user created/modified but NOT yet saved to backend
 * evenly/l12m are calculated on demand, saved custom values come from planConfigCache
 */
export type PendingCustomSeasonalityMap = Record<string, string[]>

export function createPlanningViewStore(input_param_comp_id: string) {
  return defineStore(
    `planning-view-store-${input_param_comp_id}`,
    () => {
      // Dynamically computed from route/UI map - automatically updates with navigation
      const activePlanningStatement = computed<StcsFstmtEnum>(() => {
        const fstmtView = useCtxStore().ref_comp_map[input_param_comp_id]?.ui_map?.plan?.fstmt_view
        return PlanningStatementMapper.stringToFinancialStatement(fstmtView || 'pnl')
      })
      const ctxStore = useCtxStore()

      const selectedPlanID: Ref<string | undefined> = ref(undefined)
      const selectedPlan: Ref<FinancialsGridDataAvailablePlanList | undefined> = ref(undefined)
      const plans = ref<Record<string, PlanData>>({})
      const selectedPlanState = ref<PlanData | undefined>(plans?.value[0])
      const availablePlans = computed(() => ctxStore.ref_comp_map[ctxStore.active_comp_id]?.availablePlanList ?? [])
      const seas_entry_map = ref<Record<string, seas_entry_helper>>({}) // Single source of truth for seasonality selection per row
      const pendingCustomSeasonality = ref<PendingCustomSeasonalityMap>({}) // Only stores unsaved custom values (keyed by seas_key)
      // LRU cache management for plans
      const planAccessOrder = ref<string[]>([]) // Most recently accessed plan IDs (newest first)
      const isPlanActive = computed(() => !selectedPlanState.value?.is_draft)
      const selectedView: Ref<PlanningGridDataView> = ref(PlanningGridDataView.MonthlyView)
      const fsdStore = useFsdStore()

      const planSelectLoader = ref(false)

      // Tracks whether the available plan list API has been resolved (either with plans or empty).
      // Used by PlanningsView to avoid showing "No plans exist" before the API call completes.
      const planListSettled = ref(false)

      // Loading state for updatePlanningData API calls
      const isUpdatingPlanningData = ref(false)

      // Plan selector dropdown state (shared between components)
      const planSelectorOpen = ref(false)

      // Active method dropdown row ID (to ensure only one is open at a time)
      const activeMethodDropdownRowId = ref<string | null>(null)

      const planDateRange: Ref<{ period_begin?: Date; period_end?: Date }> = ref({
        period_begin: undefined,
        period_end: undefined,
      })

      const lastActualsDate = ref<Record<string, string>>({})
      const userHasSetActualsDate = ref<Record<string, boolean>>({}) // Track if user has manually set the date

      // Plan configuration caching for direct API access
      const planConfigCache = ref<Record<string, PlanConfigCacheItem>>({}) // Cache for plan configs by plan ID
      const planConfigCacheAccessOrder = ref<string[]>([]) // LRU cache management for plan configs

      function hasUserSetActualsDate() {
        return !!userHasSetActualsDate.value[useCtxStore().active_comp_id]
      }

      const minDate = ref(new Date())
      const maxDate = ref(new Date())

      const allowedFpnaTypes = [
        StcsFpnaTypeEnum.SFT_TOTAL_CML,
        StcsFpnaTypeEnum.SFT_TOTAL_NCML,
        StcsFpnaTypeEnum.SFT_LEVEL_2,
        StcsFpnaTypeEnum.SFT_LEVEL_1,
        StcsFpnaTypeEnum.SFT_SUB_TOTAL,
        StcsFpnaTypeEnum.SFT_CTX_LINE_IPO,
        StcsFpnaTypeEnum.SFT_CTX_LINE_GROWTH,
      ]

      const lastActionPlanningList = ref<LastActionPlanList>(<LastActionPlanList>{
        [StcsFstmtEnum.SFSE_PNL]: {},
        [StcsFstmtEnum.SFSE_BS]: {},
        [StcsFstmtEnum.SFSE_CF]: {},
      })

      const selDateRangeStrategy = ref<DateRangeStrategy>()
      const lastActualDate = computed(() => getDisplayDateFromPlanningBegin(selectedPlan?.value?.fpna_begin_date_string))
      const valuesMapPnl = computed(() => selectedPlanState.value?.valuesMapPnl || {})
      const valuesMapBs = computed(() => selectedPlanState.value?.valuesMapBs || {})
      const valuesMapCf = computed(() => selectedPlanState.value?.valuesMapCf || {})
      const fsdOrg = useCtxStore().ref_fsd_map[useCtxStore().active_org_id]
      const lastAppliedRanges = ref<Record<string, string>>({})

      function setDateRangeStrategy(strategy: DateRangeStrategy) {
        selDateRangeStrategy.value = strategy
      }

      function setPlanningStatementView(view: PlanningGridDataView) {
        if (selectedView.value !== view) {
          if (import.meta.client) {
            const segment: DisplayMode = `${PlanningGridDataViewMapper.gridDataViewToString(selectedView.value)}`

            // Find the last index of the segment
            const path = window.location.href
            const lastIndex = path.lastIndexOf(`/${segment}`)
            const newPath = window.location.href.replace(`${path.substring(0, lastIndex)}/${segment}`, `${path.substring(0, lastIndex)}/${PlanningGridDataViewMapper.gridDataViewToString(view)}`)
            window.history.pushState({}, '', newPath)
          }

          selectedView.value = view

          // Initialize date range strategy for Monthly View if not already set
          if (view === PlanningGridDataView.MonthlyView && !selDateRangeStrategy.value) {
            const currentDate = new Date()
            const fyeMonth = getCompEndMonth()
            selDateRangeStrategy.value = new YearToDateStrategy(currentDate, fyeMonth)
          }

          useCtxStore().ref_comp_map[input_param_comp_id] = {
            ...useCtxStore().ref_comp_map[input_param_comp_id],
            ui_map: {
              ...useCtxStore().ref_comp_map[input_param_comp_id].ui_map,
              plan: {
                ...useCtxStore().ref_comp_map[input_param_comp_id].ui_map.plan,
                display_mode: PlanningGridDataViewMapper.gridDataViewToString(view),
              },
            },
          }
        }
      }

      function setPlanningStatement(finStmt: StcsFstmtEnum) {
        const currentFstmt = activePlanningStatement.value
        if (currentFstmt !== finStmt) {
          if (import.meta.client) {
            const segment: string = `${PlanningStatementMapper.planningStatementToString(currentFstmt)}`

            // Find the last index of the segment
            const path = window.location.href
            const lastIndex = path.lastIndexOf(`/${segment}`)
            const newPath = window.location.href.replace(`${path.substring(0, lastIndex)}/${segment}`, `${path.substring(0, lastIndex)}/${PlanningStatementMapper.planningStatementToString(finStmt)}`)
            window.history.pushState({}, '', newPath)
          }
        }

        // Update UI map - this will automatically update activePlanningStatement computed
        useCtxStore().ref_comp_map[input_param_comp_id] = {
          ...useCtxStore().ref_comp_map[input_param_comp_id],
          ui_map: {
            ...useCtxStore().ref_comp_map[input_param_comp_id].ui_map,
            plan: {
              ...useCtxStore().ref_comp_map[input_param_comp_id].ui_map.plan,
              fstmt_view: PlanningStatementMapper.planningStatementToString(finStmt) as FstmtView,
            },
          },
        }
      }

      function performParamValidation(actualsStr: string | undefined, periodBegin: Date, periodEnd: Date): { actuals: { month: number; year: number }; range: { month: number; year: number }[] } {
        const actuals = verifyAndGetActualsUrlParam(actualsStr, periodBegin, periodEnd)
        const actualsParamStr = getActualsUrlParamStr(actuals)
        /// EVERY time we change the last actuals date, we need to validate the range first and - if applicable - update the range param
        const maxRangeDate = new Date(actuals.year, actuals.month).getTime() < maxDate.value.getTime() ? new Date(actuals.year, actuals.month) : maxDate.value
        const range = verifyAndGetRangeUrlParam(useCtxStore().ref_comp_map[input_param_comp_id].ui_map.plan.scope_params.range_date_str, minDate.value, maxRangeDate)
        const rangeParamStr = getRangeUrlParamStr(range)

        useCtxStore().ref_comp_map[input_param_comp_id].ui_map.plan.scope_params.actuals_date_str = actualsParamStr
        useCtxStore().ref_comp_map[input_param_comp_id].ui_map.plan.scope_params.range_date_str = rangeParamStr

        consola.debug(`🔵 plan-store-${input_param_comp_id}.performParamValidation x1: `, input_param_comp_id)
        consola.debug(`🔵 plan-store-${input_param_comp_id}.performParamValidation x2: `, useCtxStore().ref_comp_map[input_param_comp_id].ui_map.plan.scope_params)

        return { actuals, range }
      }

      function initActualsBoundaries(actuals_date_str: string) {
        const compData = useCtxStore().ref_comp_map[input_param_comp_id]
        if (!compData) {
          throw new Error(`Company data not found for company ID: ${input_param_comp_id}`)
        }
        watch(
          () => useCtxStore().ref_comp_map[input_param_comp_id].ref_comp_actuals_res_values,
          (ref_actuals_res_values) => {
            const { fpna_begin_date_string: startDate, fpna_end_date_string: endDate } = ref_actuals_res_values?.meta || {}
            if (startDate && endDate) {
              const periodBegin = new Date(startDate)
              const periodEnd = new Date(endDate)
              minDate.value = periodBegin
              maxDate.value = periodEnd

              const { actuals } = performParamValidation(actuals_date_str, periodBegin, periodEnd)

              selDateRangeStrategy.value = new YearToDateStrategy(
                new Date(actuals.year, actuals.month, 1),
                (() => {
                  const fyeMonth = getCompEndMonth()
                  return fyeMonth
                })(),
              )

              const rangeFromDateRangeStrategy = [
                {
                  month: selDateRangeStrategy.value.dateRange.fromDate.getMonth(),
                  year: selDateRangeStrategy.value.dateRange.fromDate.getFullYear(),
                },
                {
                  month: selDateRangeStrategy.value.dateRange.toDate.getMonth(),
                  year: selDateRangeStrategy.value.dateRange.toDate.getFullYear(),
                },
              ]
              useCtxStore().ref_comp_map[input_param_comp_id].ui_map.plan.scope_params.range_date_str = getRangeUrlParamStr(rangeFromDateRangeStrategy)
            }
          },
          { immediate: true },
        )
      }

      function validateAndUpdateScopeParams(selDate: { month: number; year: number }) {
        consola.debug(`🔵 plan-store-${input_param_comp_id}.validateAndUpdateScopeParams: `, selDate)
        const { actuals } = performParamValidation(getDateStr(selDate), minDate.value, maxDate.value)
        if (!selDateRangeStrategy.value) {
          throw new Error('selDateRangeStrategy is undefined in onLastActualsDate')
        }
        selDateRangeStrategy.value.updateData(new Date(actuals.year, actuals.month, 1))

        const rangeFromDateRangeStrategy = [
          {
            month: selDateRangeStrategy.value.dateRange.fromDate.getMonth(),
            year: selDateRangeStrategy.value.dateRange.fromDate.getFullYear(),
          },
          {
            month: selDateRangeStrategy.value.dateRange.toDate.getMonth(),
            year: selDateRangeStrategy.value.dateRange.toDate.getFullYear(),
          },
        ]
        useCtxStore().ref_comp_map[input_param_comp_id].ui_map.plan.scope_params.range_date_str = getRangeUrlParamStr(rangeFromDateRangeStrategy)
      }

      /** Build once when you load the data (kept in Pinia store) */
      function makeReactiveRowList(rows: PlanningRowData[]): ReactiveRowList {
        const idxMap = new Map<string, number>()
        rows.forEach((r, i) => idxMap.set(r.row_id, i))
        return { list: rows, idxMap, idSet: new Set(idxMap.keys()) }
      }

      /** O(1) insertion-index resolver */
      function getInsertionIndex(ctx: ReactiveRowList, relativeToId?: string, insertIndex = -1, insertAfter = false): number {
        if (isExplicitIndex(insertIndex, ctx.list.length)) {
          return insertIndex
        }

        if (isRelativeToIdValid(relativeToId, ctx.idxMap)) {
          if (relativeToId !== undefined) {
            return getRelativeIndex(ctx.idxMap, relativeToId, insertAfter, ctx.list.length)
          }
          return ctx.list.length
        }

        return ctx.list.length
      }

      function isExplicitIndex(insertIndex: number, listLength: number): boolean {
        return insertIndex >= 0 && insertIndex <= listLength
      }

      function isRelativeToIdValid(relativeToId: string | undefined, idxMap: Map<string, number>): boolean {
        return !!relativeToId && idxMap.has(relativeToId)
      }

      function getRelativeIndex(idxMap: Map<string, number>, relativeToId: string, insertAfter: boolean, listLength: number): number {
        const base = idxMap.get(relativeToId)
        if (base === undefined) {
          return listLength
        }
        return insertAfter ? base + 1 : base
      }

      /** Add a row – O(N-shifted) worst-case, O(1) look-up */
      function addRow(row: PlanningRowData, planningStatement: StcsFstmtEnum, relativeToId?: string, insertIndex = -1, insertAfter = false) {
        const ctx = getCtx(planningStatement) // returns ReactiveRowList
        if (!ctx || ctx.idSet.has(row.row_id)) {
          return
        }

        const pos = getInsertionIndex(ctx, relativeToId, insertIndex, insertAfter)

        // 1) Splice the reactive array
        ctx.list.splice(pos, 0, row)

        // 2) Update id cache
        ctx.idSet.add(row.row_id)

        // 3) Update index map – shift only the tail
        for (let i = pos; i < ctx.list.length; i++) {
          ctx.idxMap.set(ctx.list[i].row_id, i)
        }
      }

      /** Remove a row – O(N-shifted) update of maps, O(1) look-up */
      function removeRow(rowId: string, planningStatement: StcsFstmtEnum) {
        const ctx = getCtx(planningStatement)
        if (!ctx) {
          return
        }
        if (!ctx.idxMap.has(rowId)) {
          return
        }

        const index = ctx.idxMap.get(rowId)
        if (index === undefined) {
          return
        }

        const [removedRow] = ctx.list.splice(index, 1)
        ctx.idSet.delete(rowId)
        ctx.idxMap.delete(rowId)

        // Shift indices for the tail only
        for (let i = index; i < ctx.list.length; i++) {
          ctx.idxMap.set(ctx.list[i].row_id, i)
        }

        /* original business logic: update parent totals */
        const parentPath = removedRow.path_level.slice(0, -1)
        const parentRowId = parentPath[parentPath.length - 1]
        updatePlanningData(parentRowId, false, true)
      }

      /* Utility – maps statement → its reactive list wrapper */
      function getCtx(stmt: StcsFstmtEnum): ReactiveRowList | undefined {
        const planId = selectedPlanID.value
        if (!planId) return undefined

        switch (stmt) {
          case StcsFstmtEnum.SFSE_PNL:
            return makeReactiveRowList(plans.value[planId]?.pnlList || [])
          case StcsFstmtEnum.SFSE_BS:
            return makeReactiveRowList(plans.value[planId]?.bsList || [])
          case StcsFstmtEnum.SFSE_CF:
            // CF data is sourced from ctx store, not FSD
            return undefined
          default:
            return undefined
        }
      }

      function updateStatementAfterDrag(newData: PlanningRowData[], planningStatement: StcsFstmtEnum) {
        if (!selectedPlanState.value) {
          consola.warn('No selected plan state available for drag update')
          return
        }

        switch (planningStatement) {
          case StcsFstmtEnum.SFSE_PNL:
            selectedPlanState.value.pnlList = newData
            break
          case StcsFstmtEnum.SFSE_BS:
            selectedPlanState.value.bsList = newData
            break
          case StcsFstmtEnum.SFSE_CF:
            // CF data is sourced from ctx store, not managed here
            consola.warn('CF drag operations not supported - data sourced from ctx store')
            break
          default:
            return
        }
      }

      function updatePlanningData(parentRowId?: string, updateLastAction = true, skipApi = false, successCb?: (response: MultiGenericPayloadResponse) => void, errorCb?: (error: Error) => void) {
        // Get current plan state
        const currentPlanState = selectedPlanState.value
        if (!currentPlanState) {
          consola.warn('No plan state available for update')
          return
        }

        // Handle skipApi cases
        if (skipApi) {
          if (updateLastAction && parentRowId) {
            updateLastActionPlanList(parentRowId)
            consola.info(`[updatePlanningData] Skipped API, updated last action for parentRowId: ${parentRowId}`)
          }
          return
        }

        // Set loading state to true
        isUpdatingPlanningData.value = true

        // Enhanced planning data update using unified edit API with automatic cancellation
        try {
          // Call the unified edit API for value updates
          // cancelPrevious: true will automatically cancel any pending API request
          callEditAPI({
            cancelPrevious: true, // Cancel previous pending request if exists
            successCallback: (response) => {
              processUpdatePlanningDataResponse(response, parentRowId, updateLastAction)
              isUpdatingPlanningData.value = false
              successCb?.(response)
            },
            errorCallback: (error) => {
              consola.error('Failed to update planning data:', error)
              isUpdatingPlanningData.value = false
              errorCb?.(error)
            },
          })
        } catch (error) {
          consola.error('Error calling unified edit API:', error)
          isUpdatingPlanningData.value = false
          errorCb?.(error as Error)
        }
      }

      // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
      const processUpdatePlanningDataResponse = (response: MultiGenericPayloadResponse, parentRowId?: string, updateLastAction = true) => {
        consola.log('DEVELOP:edit_api Response:', response)

        const payloadOfTransformation = {
          stcs_comp_plan_cfg: response.res_payload?.stcs_comp_plan_cfg as StcsCompPlanCfg,
          stcs_comp_plan_res_values: response.res_payload?.stcs_comp_plan_res_values as StcsCompPlanResValues,
        }

        // Transform plan config to result values
        const currentPlanResValues = transformPlanConfigToResValues(payloadOfTransformation)

        // Get store and data
        const ctxStore = useCtxStore()
        const compData = ctxStore.ref_comp_map[input_param_comp_id]
        const actualsPnlMap = compData.ref_comp_actuals_res_values?.pnl_map ?? {}
        const actualsBsMap = compData.ref_comp_actuals_res_values?.bs_map ?? {}
        const actualsCfMap = compData.ref_comp_actuals_res_values?.cf_map ?? {}
        const planPnlMap = currentPlanResValues?.pnl_map ?? {}
        const planBsMap = currentPlanResValues?.bs_map ?? {}
        const planCfMap = currentPlanResValues?.cf_map ?? {}

        // Merge actuals and plan maps based on last actual date
        const lastDt = lastActualDate.value
        const selectedPlanId = selectedPlanID.value || ''
        const mergedPnlMap = lastDt ? mergeActualsAndPlanMaps(actualsPnlMap, planPnlMap, new Date(lastDt)) : actualsPnlMap
        const mergedBsMap = lastDt ? mergeActualsAndPlanMaps(actualsBsMap, planBsMap, new Date(lastDt)) : actualsBsMap
        const mergedCfMap = lastDt ? mergeActualsAndPlanMaps(actualsCfMap, planCfMap, new Date(lastDt)) : actualsCfMap

        // Update state
        if (selectedPlanState.value) {
          selectedPlanState.value.valuesMapPnl = { ...selectedPlanState.value.valuesMapPnl, ...mergedPnlMap }
          selectedPlanState.value.valuesMapBs = { ...selectedPlanState.value.valuesMapBs, ...mergedBsMap }
          selectedPlanState.value.valuesMapCf = { ...selectedPlanState.value.valuesMapCf, ...mergedCfMap }
          selectedPlanState.value.is_draft = payloadOfTransformation.stcs_comp_plan_cfg?.is_draft
        }
        if (selectedPlanId in plans.value) {
          plans.value[selectedPlanId].valuesMapPnl = { ...plans.value[selectedPlanId].valuesMapPnl, ...mergedPnlMap }
          plans.value[selectedPlanId].valuesMapBs = { ...plans.value[selectedPlanId].valuesMapBs, ...mergedBsMap }
          plans.value[selectedPlanId].valuesMapCf = { ...plans.value[selectedPlanId].valuesMapCf, ...mergedCfMap }
          plans.value[selectedPlanId].is_draft = payloadOfTransformation.stcs_comp_plan_cfg?.is_draft
        }

        // also update ctx store company data
        updateCompanyPlanResValues(currentPlanResValues)

        planConfigCache.value[selectedPlanId] = payloadOfTransformation

        // Recalculate all context rows (FSD + custom rows) after data update
        recalcAllCtxRows()

        // Update last action history if needed
        if (updateLastAction && parentRowId) {
          updateLastActionPlanList(parentRowId)
        }
      }

      function setPnlCtxReference(baseRowId: string, referenceRowId: string) {
        if (!selectedPlanState.value) return

        const idx = selectedPlanState.value.pnlList.findIndex((item) => item.row_id === baseRowId)
        if (idx !== -1) {
          selectedPlanState.value.pnlList[idx].ctx_reference_row_id = referenceRowId
        }
      }

      function setBsCtxReference(baseRowId: string, referenceRowId: string) {
        if (!selectedPlanState.value) return

        const idx = selectedPlanState.value.bsList.findIndex((item) => item.row_id === baseRowId)
        if (idx !== -1) {
          selectedPlanState.value.bsList[idx].ctx_reference_row_id = referenceRowId
        }
      }

      /**
       * Helper function to add position rows from plan configuration to the FSD list
       */
      async function addPositionRowsToList(baseList: PlanningRowData[], statement: StcsFstmtEnum): Promise<PlanningRowData[]> {
        const planConfig = await getValidPlanConfig()
        if (!planConfig) return baseList

        const statementMap = getStatementMap(planConfig?.stcs_comp_plan_cfg as StcsCompPlanCfg, statement)
        if (!statementMap) return baseList

        return enhanceListWithPositions(baseList, statementMap)
      }

      /**
       * Get valid plan configuration or return null
       */
      async function getValidPlanConfig(): Promise<PlanConfigCacheItem | null> {
        const planId = selectedPlanID.value
        if (!planId) return null

        return await getPlanConfigSafely(planId)
      }

      /**
       * Safely get plan configuration
       */
      async function getPlanConfigSafely(planId: string): Promise<PlanConfigCacheItem | null> {
        try {
          return await getPlanConfig(planId)
        } catch (error) {
          consola.error('� Error getting plan config:', error)
          return null
        }
      }

      /**
       * Get statement map from plan configuration
       */
      function getStatementMap(planConfig: StcsCompPlanCfg, statement: StcsFstmtEnum): Record<string, StcsCompPlanCfgItem> | null {
        return statement === StcsFstmtEnum.SFSE_PNL ? planConfig.pnl_map : planConfig.bs_map
      }

      /**
       * Enhanced list with position rows from statement map
       */
      function enhanceListWithPositions(baseList: PlanningRowData[], statementMap: Record<string, StcsCompPlanCfgItem>): PlanningRowData[] {
        const enhancedList = [...baseList]

        for (const [itemId, itemConfig] of Object.entries(statementMap)) {
          // Find parent row and its index
          const parentIndex = enhancedList.findIndex((row) => row.row_id === itemId)
          if (parentIndex === -1) continue

          const parentRow = enhancedList[parentIndex]

          // Update parent row with config properties
          enhancedList[parentIndex] = {
            ...parentRow,
            is_struct_item: itemConfig.is_struct_item,
            status: itemConfig.is_done ? 'done' : 'open',
            is_position_row: false, // Ensure parent is not marked as position row
            calc_status: itemConfig.status,
          }

          // Add position rows if pos_map exists
          if (itemConfig.pos_map) {
            const positionRows = createPositionRowsFromConfig(parentRow, itemConfig.pos_map)
            enhancedList.splice(parentIndex + 1, 0, ...positionRows)
          }
        }

        return enhancedList
      }

      /**
       * Helper function to create position rows from pos_map configuration
       *
       * Note: Positions are sorted by pos_index to maintain proper ordering from backend.
       * This fixes the issue where backend returns positions in different order than sent
       * (e.g., sending pos_1, pos_2, pos_3 but receiving pos_1, pos_3, pos_2).
       */
      function createPositionRowsFromConfig(parentRow: PlanningRowData, posMap: Record<string, PlanPosItem>): PlanningRowData[] {
        const positionRows: PlanningRowData[] = []

        // Convert posMap to array and sort by pos_index to maintain proper ordering
        const sortedPositions = Object.entries(posMap).sort(([, a], [, b]) => {
          const indexA = a.pos_index ?? 0
          const indexB = b.pos_index ?? 0
          return indexA - indexB
        })

        for (const [positionKey, positionValue] of sortedPositions) {
          const selectedMethod = positionValue.pos_logic_type

          // Create the main position row
          const positionRow = createPositionRow(parentRow, positionKey, positionValue, selectedMethod)

          // Handle formula-based positions with sub-positions
          if (selectedMethod === MethodologyEnum.FORMULA_BASED && positionValue.formula_based?.sub_pos_map) {
            positionRows.push(positionRow)
            const subPositionRows = createSubPositionRows(parentRow, positionKey, positionValue)
            positionRows.push(...subPositionRows)
            continue // Skip adding position row again since we already added it
          }

          // Add additional methodology-specific rows before the main position row
          addMethodologySpecificRows(positionRows, positionRow, selectedMethod)

          // Add the main position row
          positionRows.push(positionRow)
        }

        return positionRows
      }

      /**
       * Creates a single position row from configuration
       */
      function createPositionRow(parentRow: PlanningRowData, positionKey: string, positionValue: PlanPosItem, selectedMethod: MethodologyEnum): PlanningRowData {
        return {
          fstmt_type: parentRow.fstmt_type,
          row_id: positionKey,
          level_id: positionKey,
          path_level: [...parentRow.path_level, positionKey],
          fpna_type: StcsFpnaTypeEnum.SFT_LEVEL_2,
          rowClass: 'custom-row',
          ref_id_mirror: undefined,
          ctx_reference_row_id: undefined,
          is_position_row: true,
          is_struct_item: positionValue.is_struct_item,
          options: parentRow.options,
          selectedMethod,
          calc_status: positionValue.status,
        }
      }

      /**
       * Creates sub-position rows for formula-based positions
       */
      function createSubPositionRows(parentRow: PlanningRowData, positionKey: string, positionValue: PlanPosItem): PlanningRowData[] {
        const subPositionRows: PlanningRowData[] = []

        if (!positionValue.formula_based?.sub_pos_map) {
          return subPositionRows
        }
        // Sort sub-positions by pos_index to maintain proper ordering
        const sortedSubPositions = Object.entries(positionValue.formula_based.sub_pos_map).sort(([, a], [, b]) => {
          const indexA = a.pos_index ?? 0
          const indexB = b.pos_index ?? 0
          return indexA - indexB
        })

        for (const [subPositionKey, subPositionValue] of sortedSubPositions) {
          const subPositionMethod = subPositionValue.pos_logic_type

          // Create sub-position row
          const subPositionRow: PlanningRowData = {
            fstmt_type: subPositionValue.fstmt_type,
            row_id: subPositionKey,
            level_id: subPositionKey,
            path_level: [...parentRow.path_level, positionKey, subPositionKey],
            fpna_type: StcsFpnaTypeEnum.SFT_LEVEL_2,
            rowClass: 'sub-custom-row',
            ref_id_mirror: undefined,
            ctx_reference_row_id: undefined,
            is_position_row: true,
            is_struct_item: subPositionValue.is_struct_item,
            options: parentRow.options,
            selectedMethod: subPositionMethod,
            outputType: subPositionValue?.display_value_type,
            calc_status: subPositionValue.status,
          }

          subPositionRows.push(subPositionRow)

          // Add methodology-specific row for monthly growth
          if (subPositionMethod === MethodologyEnum.MONTHLY_GROWTH_RATE) {
            const growthRowData = createBaseRow(subPositionRow, subPositionMethod)
            subPositionRows.push(growthRowData)
            updateI18nLabelMap(getLevelIdForMethod(subPositionMethod), getLabels(getLevelIdForMethod(subPositionMethod)))
          }
        }

        return subPositionRows
      }

      /**
       * Adds methodology-specific rows (growth, manual change, etc.) before the main position row
       */
      function addMethodologySpecificRows(positionRows: PlanningRowData[], positionRow: PlanningRowData, selectedMethod: MethodologyEnum): void {
        if (selectedMethod === MethodologyEnum.MONTHLY_GROWTH_RATE || selectedMethod === MethodologyEnum.MANUAL_DEC_INPUT_IN_CHANGE) {
          const growthRowData = createBaseRow(positionRow, selectedMethod)
          positionRows.push(growthRowData)
          updateI18nLabelMap(getLevelIdForMethod(selectedMethod), getLabels(getLevelIdForMethod(selectedMethod)))
        } else if (selectedMethod === MethodologyEnum.ANNUALLY_GROWTH_RATE) {
          const growthRowData = createAnnualGrowthRemainingPositions(positionRow, selectedMethod)
          positionRows.push(...growthRowData)
          updateI18nLabelMap(`${getLevelIdForMethod(selectedMethod)}_1`, getLabels(`${getLevelIdForMethod(selectedMethod)}_1`))
          updateI18nLabelMap(`${getLevelIdForMethod(selectedMethod)}_2`, getLabels(`${getLevelIdForMethod(selectedMethod)}_2`))
        }
      }

      function createBaseRow(row: PlanningRowData, method: MethodologyEnum): PlanningRowData {
        const newId = `${row.row_id}_${getLevelIdForMethod(method)}`
        const growthRowData = {
          ...row,
          row_id: newId,
          path_level: [...row.path_level, newId],
          level_id: getLevelIdForMethod(method),
          rowClass: 'selected-method-row',
          fpna_type: StcsFpnaTypeEnum.SFT_LEVEL_2,
          ref_id_mirror: undefined,
          options: row?.options,
          selectedMethod: undefined,
          is_position_row: false,
        }
        return growthRowData
      }

      // --- initializeHistory moved to top-level scope so it can be used in multiple places ---
      function initializeHistory(list: PlanningRowData[], statement: StcsFstmtEnum) {
        const planId = selectedPlanID.value
        if (!planId) return
        if (!plans.value[planId]) return

        // Select correct valuesMap for statement
        let valuesMap: Record<string, StcsCompActualsResValuesItem> = {}
        if (statement === StcsFstmtEnum.SFSE_PNL) {
          valuesMap = valuesMapPnl.value || {}
        } else if (statement === StcsFstmtEnum.SFSE_BS) {
          valuesMap = valuesMapBs.value || {}
        }

        const parentRowIds = Array.from(new Set(list.filter((row) => !row.is_position_row).map((item) => item.row_id)))
        parentRowIds.forEach((parentRowId) => {
          ensurePlanParentHistory(planId, statement, parentRowId)
          const children = list.filter((item) => item.path_level.includes(parentRowId))
          // Build values map for children
          const childrenValuesMap: Record<string, StcsCompActualsResValuesItem> = {}
          for (const child of children) {
            if (valuesMap[child.row_id]) {
              childrenValuesMap[child.row_id] = JSON.parse(JSON.stringify(valuesMap[child.row_id]))
            }
          }
          const planHistory = plans.value[planId].lastActionPlanningList?.[statement]
          if (planHistory?.[parentRowId]) {
            planHistory[parentRowId].lastAction = [[children, childrenValuesMap]]
            planHistory[parentRowId].activeIndex = 0
          }
        })
      }

      async function setPlanningStatementLists(fsd: StcsOrgFsdFlat | undefined) {
        if (!fsd) {
          return
        }

        // Filter FSD lists to include only allowed fpna_types and create deep copies to avoid mutating org-level FSD data
        const filteredFstmtList: PlanningRowData[] = fsd.pnl_list?.filter((item) => allowedFpnaTypes.includes(item.fpna_type)).map((item) => JSON.parse(JSON.stringify(item))) || []

        const filteredFstmtListBs = fsd.bs_list?.filter((item) => allowedFpnaTypes.includes(item.fpna_type)).map((item) => JSON.parse(JSON.stringify(item))) || []

        if (!selectedPlanState.value) {
          consola.warn('No selected plan state available for setting statement lists')
          return
        }

        // Add position rows from plan configuration
        selectedPlanState.value.pnlList = await addPositionRowsToList(filteredFstmtList, StcsFstmtEnum.SFSE_PNL)
        selectedPlanState.value.bsList = await addPositionRowsToList(filteredFstmtListBs, StcsFstmtEnum.SFSE_BS)

        // CF data is sourced from ctx store's ref_comp_actuals_res_fstmt.cf_list, not from FSD

        // Initialize history for each parent row in the selected plan (first pass, may be empty values)
        initializeHistory(selectedPlanState.value.pnlList, StcsFstmtEnum.SFSE_PNL)
        initializeHistory(selectedPlanState.value.bsList, StcsFstmtEnum.SFSE_BS)
        // CF history initialization removed - data sourced from ctx store
      }

      // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: Plan-specific history management requires conditional logic
      function updateLastActionPlanList(parentRowId: string) {
        const statement = activePlanningStatement.value
        const planId = selectedPlanID.value
        if (!planId) return

        ensurePlanParentHistory(planId, statement, parentRowId)
        const plan = plans.value[planId]
        if (!plan?.lastActionPlanningList) return

        // --- collect the fresh snapshot from plan-specific data -------------------------
        // Find direct children of the parentRowId (not all descendants)
        const parentPathLevel = statement === StcsFstmtEnum.SFSE_PNL ? plan.pnlList.find((item) => item.row_id === parentRowId)?.path_level : plan.bsList.find((item) => item.row_id === parentRowId)?.path_level

        if (!parentPathLevel) return

        const list = statement === StcsFstmtEnum.SFSE_PNL ? plan.pnlList : plan.bsList
        const allowedFpnaTypes = [StcsFpnaTypeEnum.SFT_ACC_ITEM, StcsFpnaTypeEnum.SFT_LEVEL_3, StcsFpnaTypeEnum.SFT_LEVEL_2, StcsFpnaTypeEnum.SFT_CTX_LINE_IPO, StcsFpnaTypeEnum.SFT_CTX_LINE_GROWTH]
        const filteredList = list.filter((item) => allowedFpnaTypes.includes(item.fpna_type))

        // Find all descendants: rows that have parentRowId in their path_level
        // This works regardless of which levels are filtered in/out
        const currentList = filteredList.filter((row) => {
          return row.path_level.includes(parentRowId) && row.row_id !== parentRowId
        })
        const deepClonedList = JSON.parse(JSON.stringify(currentList))
        const mapRef = statement === StcsFstmtEnum.SFSE_PNL ? plan.valuesMapPnl || {} : plan.valuesMapBs || {}
        const deepClonedValuesMap = deepCloneValuesMap(currentList, mapRef)

        // --- merge it into plan-specific history ----------------------------------------
        const statementHistory = plan.lastActionPlanningList[statement]
        if (!statementHistory?.[parentRowId]) return

        const history = statementHistory[parentRowId]
        const lastAction = history.lastAction.slice(0, history.activeIndex + 1)
        history.lastAction = [...lastAction, [deepClonedList, deepClonedValuesMap]]
        history.activeIndex++

        // --- ✂️ prune to MAX_HISTORY_LENGTH ✂️ ----------------------------------------
        if (history.lastAction.length > MAX_HISTORY_LENGTH) {
          // drop the oldest snapshot
          history.lastAction.shift()
          // move the pointer back one step so it still references the same logical item
          history.activeIndex = Math.max(history.activeIndex - 1, 0)
        }
      }

      function deepCloneValuesMap(currentList: PlanningRowData[], mapRef: Record<string, StcsCompActualsResValuesItem>): Record<string, StcsCompActualsResValuesItem> {
        const clonedValuesMap: Record<string, StcsCompActualsResValuesItem> = {}
        for (const row of currentList) {
          const rid = row.row_id
          if (mapRef[rid]) {
            clonedValuesMap[rid] = JSON.parse(JSON.stringify(mapRef[rid]))
          }
        }
        return clonedValuesMap
      }

      // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
      function updatePlanListWithHistory(planId: string, statement: StcsFstmtEnum, parentRowId: string, history: PlanningHistoryItem, targetIndex: number): void {
        if (!selectedPlanState.value) return

        const plan = plans.value[planId]
        if (!plan) return

        // Get plan-specific lists and values map
        const targetList = statement === StcsFstmtEnum.SFSE_PNL ? plan.pnlList : plan.bsList
        const valuesMapRef = statement === StcsFstmtEnum.SFSE_PNL ? plan.valuesMapPnl || {} : plan.valuesMapBs || {}

        // Skip CF operations - data sourced from ctx store
        if (statement === StcsFstmtEnum.SFSE_CF) {
          consola.warn('updatePlanListWithHistory: CF operations not supported - data sourced from ctx store')
          return
        }

        const currentList = [...targetList]
        const [snapshotRows, snapshotValuesMap] = history.lastAction[targetIndex]

        // 1. Find the parent's position
        const parentIndex = currentList.findIndex((row) => row.row_id === parentRowId)
        if (parentIndex === -1) {
          return // Parent not found, exit early
        }

        const parentRow = currentList[parentIndex]

        // 2. Prune out *all* descendants of that parent (any deeper level)
        // A row is a descendant if parentRowId exists in its path_level
        // This works regardless of which levels are filtered in/out
        const pruned = currentList.filter((row) => {
          const isDescendant = row.path_level.includes(parentRowId) && row.row_id !== parentRowId
          return !isDescendant
        })

        // Delete values for all descendants from the values map
        currentList.forEach((row) => {
          const isDescendant = row.path_level.includes(parentRowId) && row.row_id !== parentRowId

          if (isDescendant) {
            delete valuesMapRef[row.row_id]
          }
        })

        // 3. Rebuild path_level for snapshot rows to match current filter state
        // This is critical because the snapshot was created under potentially different level filters
        const rebuildPathLevelsForSnapshot = (rows: PlanningRowData[], parentPath: string[]): PlanningRowData[] => {
          const rebuiltRows: PlanningRowData[] = []

          // Helper function to recursively rebuild path_level for a row and its descendants
          const rebuildRowAndDescendants = (row: PlanningRowData, currentParentPath: string[]): void => {
            // Build the new path_level based on current parent's path
            const newPathLevel = [...currentParentPath, row.row_id]

            // Create a copy of the row with updated path_level
            const rebuiltRow = { ...row, path_level: newPathLevel }
            rebuiltRows.push(rebuiltRow)

            // Find direct children of this row in the snapshot
            // Children are identified by row_id, not path_level (which is what we're fixing)
            const children = rows.filter((r) => {
              // For custom rows and sub-positions: check if this row is the direct parent
              // based on the stored path_level second-to-last element
              if (!r.path_level || r.path_level.length < 2) return false
              const storedParentId = r.path_level[r.path_level.length - 2]
              return storedParentId === row.row_id
            })

            // Recursively rebuild children with the new path
            children.forEach((child) => {
              rebuildRowAndDescendants(child, newPathLevel)
            })
          }

          // Find root-level children (direct children of the parent we're restoring)
          const rootChildren = rows.filter((r) => {
            if (!r.path_level || r.path_level.length < 2) return false
            const storedParentId = r.path_level[r.path_level.length - 2]
            return storedParentId === parentRowId
          })

          // Rebuild each root child and its descendants
          rootChildren.forEach((child) => {
            rebuildRowAndDescendants(child, parentPath)
          })

          return rebuiltRows
        }

        // Rebuild snapshot rows with corrected path_level based on current parent's path
        const rebuiltSnapshotRows = rebuildPathLevelsForSnapshot(snapshotRows, parentRow.path_level)

        // 4. Re-insert the entire subtree right after the original parent
        pruned.splice(parentIndex + 1, 0, ...rebuiltSnapshotRows)
        for (const row of rebuiltSnapshotRows) {
          const rid = row.row_id
          if (snapshotValuesMap[rid] !== undefined) {
            valuesMapRef[rid] = JSON.parse(JSON.stringify(snapshotValuesMap[rid]))
          }
        }

        // 5. Commit changes back to plan
        targetList.splice(0, targetList.length, ...pruned)

        // Update the current selected plan state if this is the active plan
        if (planId === selectedPlanID.value) {
          // Use Map to ensure unique entries by row_id (keeps last occurrence)
          const uniqueMap = new Map<string, PlanningRowData>()
          targetList.forEach((row) => uniqueMap.set(row.row_id, row))
          const uniqueTargetList = Array.from(uniqueMap.values())

          if (statement === StcsFstmtEnum.SFSE_PNL) {
            selectedPlanState.value.pnlList = uniqueTargetList
          } else {
            selectedPlanState.value.bsList = uniqueTargetList
          }
        }

        updatePlanningData(parentRowId, false)
      }

      function undoLastAction(parentRowId: string): void {
        const statement = activePlanningStatement.value
        const planId = selectedPlanID.value
        if (!planId) return

        ensurePlanParentHistory(planId, statement, parentRowId)
        const plan = plans.value[planId]
        if (!plan?.lastActionPlanningList) return

        const statementHistory = plan.lastActionPlanningList[statement]
        if (!statementHistory?.[parentRowId]) return

        const history = statementHistory[parentRowId]
        if (history.activeIndex <= 2) {
          return
        }

        history.activeIndex--
        updatePlanListWithHistory(planId, statement, parentRowId, history, history.activeIndex)
      }

      function redoLastAction(parentRowId: string): void {
        const statement = activePlanningStatement.value
        const planId = selectedPlanID.value
        if (!planId) return

        ensurePlanParentHistory(planId, statement, parentRowId)
        const plan = plans.value[planId]
        if (!plan?.lastActionPlanningList) return

        const statementHistory = plan.lastActionPlanningList[statement]
        if (!statementHistory?.[parentRowId]) return

        const history = statementHistory[parentRowId]
        if (history.activeIndex >= history.lastAction.length - 1) {
          return
        }

        history.activeIndex++
        updatePlanListWithHistory(planId, statement, parentRowId, history, history.activeIndex)
      }

      function ensurePlanParentHistory(planId: string, planningStatement: StcsFstmtEnum, parentRowId: string) {
        const plan = plans.value[planId]
        if (!plan) return

        // Initialize lastActionPlanningList if it doesn't exist
        if (!plan.lastActionPlanningList) {
          plan.lastActionPlanningList = {
            [StcsFstmtEnum.SFSE_PNL]: {},
            [StcsFstmtEnum.SFSE_BS]: {},
            [StcsFstmtEnum.SFSE_CF]: {},
          }
        }

        // Ensure the statement history exists
        if (!plan.lastActionPlanningList[planningStatement]) {
          plan.lastActionPlanningList[planningStatement] = {}
        }

        // Initialize history for specific statement and parent if it doesn't exist
        const statementHistory = plan.lastActionPlanningList[planningStatement]
        if (statementHistory && !statementHistory[parentRowId]) {
          statementHistory[parentRowId] = {
            activeIndex: 0,
            lastAction: [[[], {} as Record<string, StcsCompActualsResValuesItem>]],
          }
        }
      }

      // ─────────────────────────────────────────────────────────────
      // LRU Cache Management for Plans
      // ─────────────────────────────────────────────────────────────
      /**
       * LRU Cache System for Plan Data Management
       *
       * This system keeps only the 3 most recently accessed plans in memory to optimize
       * memory usage when dealing with large numbers of plans (e.g., 100+ plans).
       *
       * Key features:
       * - Automatic eviction of least recently used plans
       * - Transparent reloading of plan data when needed
       * - Memory cleanup for evicted plans
       * - Track access order for efficient LRU implementation
       *
       * Usage:
       * - Call loadPlanData(planId) to load/access a plan
       * - The system automatically manages cache and eviction
       * - Use evictPlan(planId) to manually remove a specific plan
       * - isPlanDataLoaded(planId) checks if plan is in memory
       */

      /**
       * Updates plan access order for LRU cache management
       * @param planId - Plan ID that was accessed
       */
      function updatePlanAccessOrder(planId: string) {
        // Remove existing entry if present
        const existingIndex = planAccessOrder.value.indexOf(planId)
        if (existingIndex !== -1) {
          planAccessOrder.value.splice(existingIndex, 1)
        }

        // Add to front (most recently accessed)
        planAccessOrder.value.unshift(planId)

        // Evict old plans if we exceed the cache limit
        evictOldPlansIfNeeded()
      }

      /**
       * Evicts old plans from memory if cache limit is exceeded
       */
      function evictOldPlansIfNeeded() {
        while (planAccessOrder.value.length > MAX_CACHED_PLANS) {
          const oldestPlanId = planAccessOrder.value.pop()
          if (oldestPlanId && plans.value[oldestPlanId]) {
            consola.debug(`🗑️ Evicting plan from cache: ${oldestPlanId}`)
            cleanupPlanData(oldestPlanId)
            delete plans.value[oldestPlanId]
          }
        }
      }

      /**
       * Clears seasonality state (call when switching/clearing plans)
       */
      function clearSeasonalityState() {
        seas_entry_map.value = {}
        pendingCustomSeasonality.value = {}
      }

      /**
       * Cleans up all data associated with a plan
       * @param planId - Plan ID to clean up
       */
      function cleanupPlanData(planId: string) {
        const plan = plans.value[planId]
        if (!plan) return

        // Clear plan-specific arrays
        plan.pnlList = []
        plan.bsList = []

        // Clear values maps
        plan.valuesMapPnl = {}
        plan.valuesMapBs = {}
        plan.valuesMapCf = {}

        // Clear history
        plan.lastActionPlanningList = {
          [StcsFstmtEnum.SFSE_PNL]: {},
          [StcsFstmtEnum.SFSE_BS]: {},
          [StcsFstmtEnum.SFSE_CF]: {},
        }

        // Clear seasonality state
        clearSeasonalityState()
      }

      /**
       * Checks if a plan is currently loaded in memory
       * @param planId - Plan ID to check
       * @returns true if plan data is loaded
       */
      function isPlanDataLoaded(planId: string): boolean {
        const plan = plans.value[planId]
        return !!(plan?.pnlList?.length > 0 || plan?.bsList?.length > 0)
      }

      /**
       * Forces eviction of a specific plan from cache
       * @param planId - Plan ID to evict
       */
      function evictPlan(planId: string) {
        const index = planAccessOrder.value.indexOf(planId)
        if (index !== -1) {
          planAccessOrder.value.splice(index, 1)
          if (plans.value[planId]) {
            consola.debug(`🗑️ Force evicting plan from cache: ${planId}`)
            cleanupPlanData(planId)
            delete plans.value[planId]
          }
        }
      }

      /**
       * Invalidates all cached plans so that the next loadPlanData call
       * will re-read the updated FSD data from ref_fsd_map.
       * Call this after FSD changes (add/delete rows, confirm) to keep planning in sync.
       */
      function invalidateAllPlans() {
        const planIds = Object.keys(plans.value)
        for (const planId of planIds) {
          consola.debug(`🗑️ Invalidating plan due to FSD change: ${planId}`)
          cleanupPlanData(planId)
          delete plans.value[planId]
        }
        planAccessOrder.value = []
        // Clear plan config cache so position rows are also refreshed
        planConfigCache.value = {}
        planConfigCacheAccessOrder.value = []
        // If a plan was selected, force it to reload on next access
        if (selectedPlanID.value) {
          const currentPlanId = selectedPlanID.value
          selectedPlanState.value = undefined
          selectedPlanID.value = undefined
          // Reload the currently selected plan with fresh FSD data
          loadPlanData(currentPlanId)
        }
      }

      function loadPlanningRowData(compId: string, fstmt: StcsFstmtEnum) {
        watch(
          [() => useCtxStore().ref_comp_map[compId], () => useCtxStore().ref_comp_map[compId], () => fsdOrg, () => useCtxStore().ref_comp_map[compId]?.ui_map?.plan?.scope_params, () => lastActualDate.value],
          async ([newCompData, _fsdOrg, _scopeParams, _lastActualDate]) => {
            consola.debug('Triggered loadPlanningRowData watch() in gridMonthly.store.ts')

            try {
              switch (fstmt) {
                case StcsFstmtEnum.SFSE_PNL:
                  await handlePlanningProfitLoss(newCompData)
                  break
                case StcsFstmtEnum.SFSE_BS:
                  await handlePlanningBalanceSheet(newCompData)
                  break
                case StcsFstmtEnum.SFSE_CF:
                  await handlePlanningCashFlow(newCompData)
                  break
                default:
                  consola.error(`Unknown planning statement: ${fstmt}`)
              }
            } catch (error) {
              consola.error('Error loading planning data:', error)
            }
          },
          { immediate: true },
        )
      }

      // Helper functions for plan configuration caching and API access
      function getPlanConfigFromCache(planId: string): PlanConfigCacheItem | null {
        // Check if plan config is already cached
        if (planConfigCache.value[planId]) {
          // Update access order (move to front)
          const index = planConfigCacheAccessOrder.value.indexOf(planId)
          if (index > -1) {
            planConfigCacheAccessOrder.value.splice(index, 1)
          }
          planConfigCacheAccessOrder.value.unshift(planId)
          return planConfigCache.value[planId]
        }
        return null
      }

      async function fetchAndCachePlanConfig(planId: string): Promise<PlanConfigCacheItem | null> {
        try {
          const ctxStore = useCtxStore()
          const request: CompPlanCfgGetRequest = {
            ref_id_org: ctxStore.active_org_id,
            ref_id_comp_plan_cfg: planId,
            ref_id_user: ctxStore.ref_user.id.extractId(),
          }

          const ctxI18n = ctxStore.ref_fsd_map[ctxStore.active_org_id]?.i18n_map[ctxStore.active_org_id]?.label_map || {}

          const response = await cmwCompPlanCfgGetRequest(request).toPromise()

          if (response?.res_payload?.stcs_comp_plan_cfg) {
            const payloadOfTransformation = {
              stcs_comp_plan_cfg: response.res_payload?.stcs_comp_plan_cfg as StcsCompPlanCfg,
              stcs_comp_plan_res_values: response.res_payload?.stcs_comp_plan_res_values as StcsCompPlanResValues,
            }

            if (ctxStore.ref_fsd_map[ctxStore.active_org_id]) {
              ctxStore.ref_fsd_map[ctxStore.active_org_id] = {
                ...ctxStore.ref_fsd_map[ctxStore.active_org_id],
                i18n_map: {
                  ...ctxStore.ref_fsd_map[ctxStore.active_org_id].i18n_map,
                  [ctxStore.active_org_id]: {
                    ...ctxStore.ref_fsd_map[ctxStore.active_org_id].i18n_map[ctxStore.active_org_id],
                    label_map: {
                      ...ctxI18n,
                      ...response.res_payload.stcs_comp_plan_cfg.i18n_map[ctxStore.active_org_id].label_map,
                    },
                  },
                },
              }
            }
            return cachePlanConfig(planId, payloadOfTransformation)
          }
        } catch (error) {
          consola.error('Error fetching plan configuration:', error)
        }
        return null
      }

      function cachePlanConfig(planId: string, res: PlanConfigCacheItem): PlanConfigCacheItem {
        // Add to cache
        planConfigCache.value[planId] = res

        // Update access order
        const index = planConfigCacheAccessOrder.value.indexOf(planId)
        if (index > -1) {
          planConfigCacheAccessOrder.value.splice(index, 1)
        }
        planConfigCacheAccessOrder.value.unshift(planId)

        // Clean up cache if it exceeds maximum size
        while (planConfigCacheAccessOrder.value.length > MAX_CACHED_PLAN_CONFIGS) {
          const oldestPlanId = planConfigCacheAccessOrder.value.pop()
          if (oldestPlanId) {
            delete planConfigCache.value[oldestPlanId]
          }
        }

        return res
      }

      function invalidatePlanConfigCache(planId?: string) {
        if (planId) {
          delete planConfigCache.value[planId]
          planConfigCacheAccessOrder.value = planConfigCacheAccessOrder.value.filter((id) => id !== planId)
          return
        }

        planConfigCache.value = {}
        planConfigCacheAccessOrder.value = []
      }

      async function getPlanConfig(planId: string): Promise<PlanConfigCacheItem | null> {
        // Try cache first
        const cached = getPlanConfigFromCache(planId)
        if (cached) {
          return cached
        }

        // Fetch from API if not cached
        return await fetchAndCachePlanConfig(planId)
      }

      function transformPlanConfigToResValues(res: PlanConfigCacheItem): StcsCompPlanResValues {
        // Transform StcsCompPlanCfg to StcsCompPlanResValues format
        // This maintains backward compatibility with existing code
        const planConfig = res?.stcs_comp_plan_cfg
        const CompPlanResValues = res?.stcs_comp_plan_res_values

        // If CompPlanResValues is provided directly, use it
        if (!CompPlanResValues) {
          consola.warn('No CompPlanResValues found in response, transforming from PlanConfig')
        }
        if (!planConfig) {
          consola.warn('No plan configuration found in response')
          return {} as StcsCompPlanResValues
        }
        consola.info('🔄 Transforming plan config to res values format:', planConfig.id)

        // Extract res_map from each plan item configuration for pnl
        const pnl_map: { [rowId: string]: StcsCompPlanResValuesItem } = {}
        Object.entries(planConfig.pnl_map || {}).forEach(([rowId, planItemCfg]) => {
          if (planItemCfg?.res_map) {
            // Extract positions for this PnL item (with inversion not applied)
            const positions = extractItemPositions(rowId, planItemCfg)
            Object.assign(pnl_map, positions)

            Object.assign(pnl_map, CompPlanResValues?.pnl_map)
          }
        })

        // Extract res_map from each plan item configuration for bs
        const bs_map: { [rowId: string]: StcsCompPlanResValuesItem } = {}
        Object.entries(planConfig.bs_map || {}).forEach(([rowId, planItemCfg]) => {
          if (planItemCfg?.res_map) {
            // Extract positions for this BS item (with inversion not applied)
            const positions = extractItemPositions(rowId, planItemCfg)
            Object.assign(bs_map, positions)

            Object.assign(bs_map, CompPlanResValues?.bs_map)
          }
        })

        // Note: CF (Cash Flow) is typically handled separately from plan config
        const cf_map: { [rowId: string]: StcsCompPlanResValuesItem } = CompPlanResValues?.cf_map || {}

        const result = {
          id: planConfig.id || '',
          version: planConfig.version || '',
          meta: planConfig?.meta || undefined, // Plan config doesn't contain meta, would need separate API call
          is_draft: true,
          pnl_map,
          bs_map,
          cf_map,
        }

        return result
      }

      /**
       * Extract position values from PlanPosItem
       * Handles the polymorphic nature of position types (oneof field)
       */
      function extractPositionValues(positionKey: string, planPosValue: PlanPosItem): { [key: string]: number } | null {
        const rawValues = getRawPositionValues(positionKey, planPosValue)

        if (!rawValues) {
          return null
        }

        return rawValues
      }

      /**
       * Helper function to extract raw position values based on position type
       */
      function getRawPositionValues(positionKey: string, planPosValue: PlanPosItem): { [key: string]: number } | null {
        const valueExtractors: Array<() => { [key: string]: number } | null> = [
          () => planPosValue.manual_dec_input?.pos_res_plan_map || null, // Manual method
          () => planPosValue.monthly_growth_rate?.pos_res_plan_map || null, // Monthly growth method
          () => planPosValue.annually_growth_rate?.pos_res_plan_map || null, // Annual growth method
          () => planPosValue.formula_based?.pos_res_plan_map || null, // Formula-based method
          () => planPosValue.retained_earnings?.pos_res_plan_map || null, // Retained earnings method
          () => (planPosValue.manual_in_change_ref_yearly ? {} : null), // Manual change reference yearly method (empty map)
          () => planPosValue.manual_dec_input_in_change?.pos_res_plan_map || null, // Manual change method
        ]

        for (const extractor of valueExtractors) {
          const result = extractor()
          if (result !== null) {
            return result
          }
        }

        consola.warn(`⚠️ Unknown position type for: ${positionKey}`, planPosValue)
        return null
      }

      /**
       * Extract all positions from a plan item configuration
       *
       * Note: Positions are sorted by pos_index to maintain consistent ordering
       * between frontend display and backend response processing.
       */
      function extractItemPositions(_itemId: string, planItemCfg: StcsCompPlanCfgItem): { [positionId: string]: StcsCompPlanResValuesItem } {
        const positions: { [positionId: string]: StcsCompPlanResValuesItem } = {}

        if (!planItemCfg.pos_map) {
          return positions
        }

        // Sort positions by pos_index to maintain proper ordering
        const sortedPositions = Object.entries(planItemCfg.pos_map).sort(([, a], [, b]) => {
          const indexA = a.pos_index ?? 0
          const indexB = b.pos_index ?? 0
          return indexA - indexB
        })

        // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
        sortedPositions.forEach(([positionKey, planPosValue]) => {
          if (planPosValue.pos_logic_type === MethodologyEnum.MONTHLY_GROWTH_RATE) {
            handleMonthlyGrowthPositions(positionKey, planPosValue)
          }
          if (planPosValue.pos_logic_type === MethodologyEnum.MANUAL_DEC_INPUT_IN_CHANGE) {
            handleManualChangePositions(positionKey, planPosValue)
          }
          if (planPosValue.pos_logic_type === MethodologyEnum.ANNUALLY_GROWTH_RATE) {
            handleAnnualGrowthPositions(positionKey, planPosValue)
          }

          if (planPosValue.pos_logic_type === MethodologyEnum.FORMULA_BASED) {
            handleFormulaBasedPositions(positionKey, planPosValue)
          }

          const positionValues = extractPositionValues(positionKey, planPosValue)
          // Add position entry if extractPositionValues returns a valid object (even if empty)
          if (positionValues !== null) {
            const positionId = `${positionKey}`
            positions[positionId] = {
              values_map: positionValues,
            }
          }
        })

        return positions
      }

      // Helper function to get current plan's res values
      async function getCurrentPlanResValues(compData: WappCompData): Promise<StcsCompPlanResValues> {
        const currentPlanId = selectedPlan.value?.id?.extractId()

        if (!currentPlanId) {
          consola.warn('No current plan ID available')
          return {} as StcsCompPlanResValues
        }

        try {
          // Try to get plan config from API first (modern approach)
          const planConfig = await getPlanConfig(currentPlanId)
          if (planConfig) {
            createInitialSeasonalityEntryWhileProcessingResponse(planConfig.stcs_comp_plan_cfg?.seas_map || {})
            // Transform plan config to res values format for backward compatibility
            return transformPlanConfigToResValues(planConfig)
          }
        } catch (error) {
          consola.error('Error fetching plan configuration, falling back to compData approach:', error)
        }

        // Fallback to old structure for backward compatibility
        if (compData.ref_comp_plans?.[currentPlanId]?.ref_comp_plan_res_values) {
          return compData.ref_comp_plans[currentPlanId].ref_comp_plan_res_values
        }

        // Final fallback
        consola.warn('No plan res values found for plan ID:', currentPlanId)
        return {} as StcsCompPlanResValues
      }

      // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
      async function handlePlanningProfitLoss(compData: WappCompData) {
        if (!selectedPlanState.value) return
        if (!selectedPlanID.value) return

        const actualsPnlMap = compData.ref_comp_actuals_res_values?.pnl_map || {}
        const currentPlanResValues = await getCurrentPlanResValues(compData)
        const planPnlMap = currentPlanResValues?.pnl_map || {}

        const lastDt = lastActualDate.value

        const mergedPnlMap = lastDt ? mergeActualsAndPlanMaps(actualsPnlMap, planPnlMap, new Date(lastDt)) : actualsPnlMap

        if (lastDt) {
          selectedPlanState.value.valuesMapPnl = { ...selectedPlanState.value.valuesMapPnl, ...mergedPnlMap }
          plans.value[selectedPlanID.value].valuesMapPnl = { ...plans.value[selectedPlanID.value].valuesMapPnl, ...mergedPnlMap }
        } else {
          selectedPlanState.value.valuesMapPnl = actualsPnlMap
          plans.value[selectedPlanID.value].valuesMapPnl = actualsPnlMap
        }

        // Now that valuesMapPnl is set, re-initialize history so children get correct values
        if (selectedPlanState.value.pnlList && selectedPlanState.value.pnlList.length > 0) {
          initializeHistory(selectedPlanState.value.pnlList, StcsFstmtEnum.SFSE_PNL)
        }

        recalcAllCtxRows()
      }

      // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
      async function handlePlanningBalanceSheet(compData: WappCompData) {
        if (!selectedPlanState.value) return
        if (!selectedPlanID.value) return

        const actualsBsMap = compData.ref_comp_actuals_res_values?.bs_map || {}
        const currentPlanResValues = await getCurrentPlanResValues(compData)
        const planBsMap = currentPlanResValues?.bs_map || {}

        const lastDt = lastActualDate.value

        const mergedBsMap = lastDt ? mergeActualsAndPlanMaps(actualsBsMap, planBsMap, new Date(lastDt)) : actualsBsMap

        if (lastDt) {
          selectedPlanState.value.valuesMapBs = { ...selectedPlanState.value.valuesMapBs, ...mergedBsMap }
          plans.value[selectedPlanID.value].valuesMapBs = { ...plans.value[selectedPlanID.value].valuesMapBs, ...mergedBsMap }
        } else {
          selectedPlanState.value.valuesMapBs = actualsBsMap
          plans.value[selectedPlanID.value].valuesMapBs = actualsBsMap
        }

        // Now that valuesMapBs is set, re-initialize history so children get correct values
        if (selectedPlanState.value.bsList && selectedPlanState.value.bsList.length > 0) {
          initializeHistory(selectedPlanState.value.bsList, StcsFstmtEnum.SFSE_BS)
        }

        recalcAllCtxRows()
      }

      async function handlePlanningCashFlow(compData: WappCompData) {
        if (!selectedPlanState.value) return
        const actualsCfMap = compData.ref_comp_actuals_res_values?.cf_map || {}
        const currentPlanResValues = await getCurrentPlanResValues(compData)
        const planCfMap = currentPlanResValues?.cf_map || {}

        const lastDt = lastActualDate.value
        if (lastDt) {
          // valuesMapCf.value = mergeActualsAndPlanMaps(actualsCfMap, planCfMap, new Date(lastDt))
          selectedPlanState.value.valuesMapCf = mergeActualsAndPlanMaps(actualsCfMap, planCfMap, new Date(lastDt))
          plans.value[selectedPlanID.value || ''].valuesMapCf = mergeActualsAndPlanMaps(actualsCfMap, planCfMap, new Date(lastDt))
        } else {
          // valuesMapCf.value = actualsCfMap
          selectedPlanState.value.valuesMapCf = actualsCfMap
          plans.value[selectedPlanID.value || ''].valuesMapCf = actualsCfMap
        }
      }

      function mergeActualsAndPlanMaps(actualsMap: { [rowId: string]: StcsCompActualsResValuesItem }, planMap: { [rowId: string]: StcsCompPlanResValuesItem }, lastActualDate: Date): { [rowId: string]: StcsCompActualsResValuesItem } {
        const merged: { [rowId: string]: StcsCompActualsResValuesItem } = {}

        const allRowIds = new Set<string>([...Object.keys(actualsMap), ...Object.keys(planMap)])

        allRowIds.forEach((rowId) => {
          merged[rowId] = {
            values_map: {},
          }

          const actualMonths = Object.keys(actualsMap[rowId]?.values_map ?? {})
          const planMonths = Object.keys(planMap[rowId]?.values_map ?? {})
          const allMonths = new Set([...actualMonths, ...planMonths])

          allMonths.forEach((yyyyMm) => {
            const dt = parseYYYYMM(yyyyMm)
            if (dt <= lastActualDate) {
              merged[rowId].values_map[yyyyMm] = actualsMap[rowId]?.values_map?.[yyyyMm]
            } else {
              merged[rowId].values_map[yyyyMm] = planMap[rowId]?.values_map?.[yyyyMm]
            }
          })
        })
        return merged
      }

      const setLastAppliedRange = (rowId: string, columnId: string, rangeId: string) => {
        const key = `${rowId}_${columnId}`
        lastAppliedRanges.value[key] = rangeId
      }

      const getLastAppliedRange = (rowId: string, columnId: string): string => {
        const key = `${rowId}_${columnId}`
        return lastAppliedRanges.value[key] || ''
      }

      async function loadPlanData(newPlanId: string) {
        planSelectLoader.value = true

        // Clear seasonality state when switching plans
        clearSeasonalityState()

        // Update access order for LRU cache
        updatePlanAccessOrder(newPlanId)
        const isDataExistInStore = isPlanDataLoaded(newPlanId)

        if (isDataExistInStore) {
          selectedPlanState.value = plans.value[newPlanId]
          selectedPlanID.value = newPlanId
          planSelectLoader.value = false
        } else {
          consola.debug(`📋 Loading plan from API: ${newPlanId}`)
          planSelectLoader.value = true

          const planIndexInAvailablePlans = availablePlans.value.findIndex((plan) => plan?.id?.extractId() === newPlanId)
          const planFromCtx = availablePlans.value[planIndexInAvailablePlans]

          // Initialize plan entry if it doesn't exist
          if (!plans.value[newPlanId]) {
            plans.value[newPlanId] = {
              id: newPlanId,
              pnlList: [],
              bsList: [],
              valuesMapPnl: {},
              valuesMapBs: {},
              valuesMapCf: {},
              lastActionPlanningList: {
                [StcsFstmtEnum.SFSE_PNL]: {},
                [StcsFstmtEnum.SFSE_BS]: {},
                [StcsFstmtEnum.SFSE_CF]: {},
              },
              // Required StcsCompPlanCfg properties
              version: planFromCtx.version,
              fpna_begin_date_string: planFromCtx.fpna_begin_date_string,
              fpna_end_date_string: planFromCtx.fpna_end_date_string,
              is_draft: planFromCtx.is_draft,
              obj_created_iso_utc: planFromCtx.obj_created_iso_utc,
              obj_updated_iso_utc: planFromCtx.obj_updated_iso_utc,
              ref_id_comp_plan_cfg_template: planFromCtx.ref_id_comp_plan_cfg_template,
              display_name: planFromCtx.display_name,
            }
          }

          // Set the selected plan state immediately
          selectedPlanState.value = plans.value[newPlanId]
          selectedPlanID.value = newPlanId

          // Check if FSD data already exists in the store for this org
          const orgId = useCtxStore().active_org_id
          const existingFsdData = useCtxStore().ref_fsd_map[orgId]

          if (existingFsdData?.pnl_list && existingFsdData?.bs_list) {
            // Use existing FSD data from store without API call
            fsdStore.setFSDStatementLists(existingFsdData)
            await setPlanningStatementLists(existingFsdData)
            planSelectLoader.value = false
          } else {
            // Load FSD data from API if not in store
            useCtxStore()
              .loadFlatFSD(orgId)
              .subscribe({
                next: async (ref_fsd_map) => {
                  if (ref_fsd_map) {
                    fsdStore.setFSDStatementLists(ref_fsd_map)
                    await setPlanningStatementLists(ref_fsd_map)
                    planSelectLoader.value = false
                  }
                },
                error: (err) => {
                  consola.error('🔴 loadFlatFSD error:', err)
                  planSelectLoader.value = false
                },
              })
          }
        }
      }
      // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: Plan result value loading requires conditional branching for error handling and data initialization
      async function loadPlanningResultValues(planId: string) {
        const ctxStore = useCtxStore()

        const planIndexInAvailablePlans = availablePlans.value.findIndex((plan) => plan.id.extractId() === planId)

        const planToSelect = availablePlans.value[planIndexInAvailablePlans]
        if (!planToSelect) return

        consola.debug('🔵 Fetching plan result values via comp_plan_cfg_get for plan:', planId)

        try {
          // Use getPlanConfig (comp_plan_cfg_get) which returns both plan config and res values
          // If loadPlanData already called getPlanConfig, this will be a cache hit (no extra API call)
          const planConfig = await getPlanConfig(planId)

          const compMap = ctxStore.ref_comp_map[ctxStore.active_comp_id]
          if (!compMap) return

          // Initialize plan data if it doesn't exist
          if (!compMap.ref_comp_plans) {
            compMap.ref_comp_plans = {}
          }
          if (!compMap.ref_comp_plans[planId]) {
            compMap.ref_comp_plans[planId] = {}
          }

          if (planConfig) {
            // Initialize seasonality entries from the plan config
            createInitialSeasonalityEntryWhileProcessingResponse(planConfig.stcs_comp_plan_cfg?.seas_map || {})

            // Transform plan config to res values format for backward compatibility
            const planResValues = transformPlanConfigToResValues(planConfig)

            // Store data in plan-specific location (same structure as before)
            compMap.ref_comp_plans[planId].ref_comp_plan_res_values = planResValues
            consola.debug('🟢 Successfully loaded plan result values for plan:', planId)

            await handlePlanningProfitLoss(compMap)
            await handlePlanningBalanceSheet(compMap)
            await handlePlanningCashFlow(compMap)
          } else {
            consola.warn('🟡 No plan config found for plan:', planId)
            const emptyValues = {} as StcsCompPlanResValues
            compMap.ref_comp_plans[planId].ref_comp_plan_res_values = emptyValues
          }
        } catch (error) {
          consola.error('🔴 loadPlanningResultValues error:', error)
        }
      }

      return {
        loadPlanningResultValues,
        selectedPlan,
        selectedPlanID,
        planSelectLoader,
        planListSettled,
        isUpdatingPlanningData,
        planSelectorOpen,
        activeMethodDropdownRowId,
        initActualsBoundaries,
        setPlanningStatement,
        activePlanningStatement,
        setPlanningStatementView,
        // subscribeLastActualsDates,
        selectedView,
        validateAndUpdateScopeParams,
        planDateRange,
        minDate,
        maxDate,
        selDateRangeStrategy,
        lastActualsDate,
        addRow,
        removeRow,
        updatePlanningData,
        processUpdatePlanningDataResponse,
        undoLastAction,
        redoLastAction,
        setPlanningStatementLists,
        lastActionPlanningList,
        selectedPlanState,
        // cfList removed - CF data sourced from ctx store
        setPnlCtxReference,
        setBsCtxReference,
        updateStatementAfterDrag,
        updateLastActionPlanList,
        loadPlanningRowData,
        valuesMapPnl,
        valuesMapBs,
        valuesMapCf,
        handlePlanningProfitLoss,
        handlePlanningBalanceSheet,
        handlePlanningCashFlow,
        setLastAppliedRange,
        getLastAppliedRange,
        lastAppliedRanges,
        pendingCustomSeasonality,
        seas_entry_map,
        clearSeasonalityState,
        setDateRangeStrategy,
        plans,
        loadPlanData,
        hasUserSetActualsDate,
        // LRU Cache Management
        updatePlanAccessOrder,
        isPlanDataLoaded,
        evictPlan,
        invalidateAllPlans,
        planAccessOrder,
        // Plan Configuration API Access
        getPlanConfig,
        invalidatePlanConfigCache,
        getCurrentPlanResValues,
        transformPlanConfigToResValues,
        extractPositionValues,
        getRawPositionValues,
        extractItemPositions,
        planConfigCache,
        planConfigCacheAccessOrder,
        isPlanActive,
      }
    },
    { persist: false },
  )()
}
